// Makes @auger/core's write refusals visible to the user.
//
// The package's write arbiters — computeRecordChanges, computeListingChanges,
// computeRelationChanges, applyRelationChanges — refuse a malformed change by
// THROWING synchronously: a resolved Apollo entity headed for jsonb, a relation
// the fetch document never hydrated (assertRelationHydrated), an undeclared
// listing context key. The fork these replaced only logged a console.warn and
// skipped, so the frontend never had to handle a refusal.
//
// Two things make an unguarded refusal invisible rather than loud:
//
//  1. Every arbiter call sits BEFORE its composable's try/catch — the try wraps
//     the mutation, and there is nothing to mutate until the arbiter has ruled.
//     So the existing toast never fires for a refusal.
//  2. The write paths are `async`, which was deliberate (a sync throw would skip
//     a caller's .catch()) but means a refusal is delivered as a rejected
//     promise — and several callers are fire-and-forget: the inline-edit commit
//     (useInteraction.commitEdit), the kanban drop, the list edit form.
//
// The result was a deliberate refusal reaching the user as "the field just
// didn't save": no toast, no console error, no telemetry. Wrapping the call
// surfaces it where the refusal is MADE, so it no longer depends on what the
// caller does with the promise.
//
// Rethrows after toasting: the caller's rollback and rejection contract is
// unchanged, this only guarantees the refusal is not silent.

import type { Toast } from "./types"

export function arbitrate<T>(subject: string, toast: Toast | null | undefined, fn: () => T): T {
  try {
    return fn()
  } catch (err) {
    const message = (err as Error).message
    console.error(`[write-arbiter] refused to update ${subject}:`, err)
    toast?.error?.(`Cannot update ${subject}: ${message}`)
    throw err
  }
}
